import { config } from "./auth";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function apiFetch<T>(
  path: string,
  token: string | undefined,
  init?: RequestInit,
): Promise<T> {
  if (!token) throw new ApiError(401, "No auth token");
  const url = `${config.apiBaseUrl}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body || res.statusText);
  }
  return (await res.json()) as T;
}

// Typed shapes for MVP endpoints (filled in as endpoints land).

export interface Kpis {
  revenue_current: number;
  revenue_prior: number;
  qty_current: number;
  qty_prior: number;
  active_customers_current: number;
  active_customers_prior: number;
  at_risk_customers: number;
  period: { from: string; to: string };
  prior_period: { from: string; to: string };
}

export type Segment = "lost" | "declining" | "stable" | "growing" | "new";

export interface SegmentedCustomer {
  customer_id: string;
  customer_name: string;
  current_qty: number;
  current_revenue: number;
  prior_qty: number;
  prior_revenue: number;
  yoy_delta_qty: number;
  yoy_delta_pct: number | null;
  segment: Segment;
  last_order_date: string | null;
  monitor_count: number;
}

export interface ReportFile {
  key: string;
  size: number;
  last_modified: string;
  kind: "daily" | "weekly" | "other";
}

export interface MonthlyYoyCell {
  year: number;
  month: number; // 1-12
  revenue: number;
  qty: number;
}

export interface MonthlyYoy {
  customer_id: string;
  customer_name: string;
  product_groups: string[];
  years: number[];
  cells: MonthlyYoyCell[];
}

export interface YearlyAccount {
  customer_id: string;
  customer_name: string;
  monitor_count: number;
  revenue: Record<string, number>; // year (string) -> revenue
  qty: Record<string, number>;
  ytd_current: { revenue: number; qty: number };
  ytd_prior: { revenue: number; qty: number };
}

export interface CustomersYearly {
  years: number[];
  current_year: number | null;
  prior_year: number | null;
  ytd_through_month: number | null; // 1-12
  product_groups: string[];
  customers: YearlyAccount[];
}

export interface VAGovtHospital {
  customer_id:       string;
  customer_name:     string;
  state:             string | null;
  idn_network:       string | null;
  hospital_category: string | null;
  street_address:    string | null;
  customer_since:    string | null;
  monitor_count:     number;
  yearly: { year: number; qty: number; revenue: number }[];
}

export interface VAGovtResponse {
  years:     number[];
  hospitals: VAGovtHospital[];
}

export type EmrStatus = "Yes" | "No" | "In progress";

export interface HospitalUnit {
  id:             number;
  hospital_name:  string;
  customer_id:    string | null;
  category:       string | null;
  cv_icu:         boolean;
  micu:           boolean;
  sicu_trauma:    boolean;
  mix_icu:        boolean;
  burn_icu:       boolean;
  neuro_icu:      boolean;
  pcu:            boolean;
  picu:           boolean;
  transplant:     boolean;
  or_unit:        boolean;
  mother_child:   boolean;
  intermed_icu:   boolean;
  er:             boolean;
  emr_connected:  EmrStatus | null;
  last_visit:     string | null;
  vendor:         string | null;
  comments:       string | null;
  updated_at:     string | null;
  updated_by:     string | null;
}

export interface HospitalUnitsResponse {
  hospitals: HospitalUnit[];
}
